import { document } from '~api';
import { Field } from '~models';
import { getField, updateField } from 'vuex-map-fields';
import { Handler } from '~services';
import router from '~router';
import camelCase from 'lodash/camelCase';
import startCase from 'lodash/startCase';
import i18n from '../i18n';

const SET_FIELDS = 'SET_FIELDS';
const RESET_FIELDS = 'RESET_FIELDS';
const SET_IMPORT_ERRORS = 'SET_IMPORT_ERRORS';
const LOADING = 'LOADING';

const state = {
    fields: [{ ...new Field() }, { ...new Field() }],
    errorsList: [],
    isLoading: false,
    showErrorsDialog: false
};

const actions = {
    /**
     * Reset fields state and redirect to users list
     * @param {function} commit
     */
    cancel({ commit }) {
        commit(RESET_FIELDS);
        router.push({ name: 'dashboard.users' });
    },

    /**
     * Create document custom fields
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    async createFields({ commit, dispatch, state }) {
        commit('LOADING');

        const { fields } = state;
        let items = [];
        fields.map(field => {
            if (field.label.length > 0) {
                items.push(field);
            }
        });
        items.map(field => (field.label = camelCase(field.label)));

        const resp = await document.createFields({ items });

        Handler.handleResponse(resp, '', '');

        if (resp.ok) {
            commit(RESET_FIELDS);

            dispatch('downloadTemplate', { root: true });
        }

        commit('LOADING');
    },

    /**
     * Download template
     * @returns {Promise<void>}
     */
    async downloadTemplate() {
        const docResp = await document.downloadTemplate();
        if (docResp.ok) {
            let blob = new Blob([docResp.data], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const link = window.document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'template.xlsx';
            link.click();
        }

        Handler.handleResponse(docResp, '', '', 'dashboard.users');
    },

    /**
     * Get document custom fields
     * @param {function} commit
     * @returns {Promise<void>}
     */
    async readFields({ commit }, id) {
        commit('LOADING');

        const resp = await document.readFields(id);

        if (resp.ok && resp.data.items && resp.data.items.length !== 0) {
            resp.data.items.map(field => {
                field.label = startCase(field.label);
                delete field.createdAt;
                delete field.updatedAt;
            });
            commit('SET_FIELDS', resp.data);
        } else {
            commit('SET_FIELDS', []);
        }

        commit('LOADING');
    },

    /**
     * Import Users from document
     * @param {function} commit
     * @param {data} object
     * @returns {Promise<void>}
     */
    async importUsers({ commit }, data) {
        commit(LOADING);

        const errorMessage = i18n.t('users.list.importError');
        const resp = await document.importUsers(data);

        commit(LOADING);
        if (!resp.ok) commit(SET_IMPORT_ERRORS, resp.data?.message);

        Handler.handleResponse(resp, undefined, errorMessage);

        return resp.ok;
    },

    /**
     * Export Users in document
     * @param {function} commit
     * @param {data} object
     * @returns {Promise<void>}
     */
    async exportUsers({ commit }, data) {
        commit('LOADING');

        const resp = await document.exportUsers(data);

        if (resp.ok) {
            let blob = new Blob([resp.data], {
                type:
                    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            const link = window.document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = 'export.xlsx';
            link.click();

            Handler.handleResponse(resp);
        }

        Handler.handleResponse(resp);

        commit('LOADING');
    },

    /**
     * Update document custom fields
     * @param {function} commit
     * @param {object} state
     * @returns {Promise<void>}
     */
    async update({ commit, state }) {
        commit('LOADING');

        const resp = await document.update(state);

        if (resp.ok) {
            commit('SET_FIELDS', resp.data);
        }

        Handler.handleResponse(resp, '', '', 'dashboard.users');

        commit('LOADING');
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,

    /**
     * Set fields data in state
     * @param {object} state
     * @param {array} data
     */
    [SET_FIELDS](state, data) {
        if (data.items && data.items.length === 1) {
            state.fields = [...data.items, { ...new Field() }];
        } else {
            state.fields = data.items || [
                { ...new Field() },
                { ...new Field() }
            ];
        }
    },

    /**
     * Reset fields data
     * @param {object} state
     */
    [RESET_FIELDS](state) {
        state.fields = [{ ...new Field() }, { ...new Field() }];
    },

    /**
     * Toggle isLoading value
     * @param {object} state
     */
    [LOADING](state) {
        state.isLoading = !state.isLoading;
    },
    /**
     * Set import errors
     * @param {object} state
     */
    [SET_IMPORT_ERRORS](state, errors) {
        const errorsList = [];
        if (errors.property) {
            errorsList.push({
                key: 'document',
                error: Object.values(errors.constraints)[0]
            });
        } else {
            Object.keys(errors).map(key => {
                errors[key].map(error =>
                    errorsList.push({
                        key,
                        error: Object.values(error.constraints)[0]
                    })
                );
            });
        }

        state.errorsList = errorsList;
        state.showErrorsDialog = true;
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    isLoading: state => state.isLoading,
    fields: state => state.fields,
    errorsList: state => state.errorsList,
    fieldsFormatted: state => {
        const fields = new Map();

        if (state.fields && state.fields.length)
            state.fields.forEach(field => fields.set(field.id, field.label));

        return Object.fromEntries(fields);
    }
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters
};
